import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  MoreVert as MoreVertIcon,
  LocalShipping as LocalShippingIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  ShoppingCart as ShoppingCartIcon,
  Campaign as CampaignIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import APIHelper from "../utils/APIHelper";
import CarrierServiceDialog from "../components/CarrierServiceDialog";
import EmptyState from "../components/discovery/EmptyState";


export default function OrdersPage({ token, pageId }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which order is being updated
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [generatingLabel, setGeneratingLabel] = useState(null); // Track which order is generating label
  const [trackingData, setTrackingData] = useState({}); // Store tracking data per order
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [selectedOrderForLabel, setSelectedOrderForLabel] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [loadingServiceability, setLoadingServiceability] = useState(false);
  const [quickQuoteRates, setQuickQuoteRates] = useState([]);
  const [loadingQuickQuote, setLoadingQuickQuote] = useState(false);
  const [packageWeight, setPackageWeight] = useState(null);
  const [packageDimensions, setPackageDimensions] = useState({
    length: null,
    width: null,
    height: null,
  });
  const [selectedRate, setSelectedRate] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedOrderForMenu, setSelectedOrderForMenu] = useState(null);
  const [cancellingShipment, setCancellingShipment] = useState(null); // Track which order is cancelling shipment

  // Custom shipment states
  const [customShipDialogOpen, setCustomShipDialogOpen] = useState(false);
  const [customCarrier, setCustomCarrier] = useState("");
  const [customTrackingNumber, setCustomTrackingNumber] = useState("");
  const [customTrackingUrl, setCustomTrackingUrl] = useState("");
  const [selectedOrderForCustomShip, setSelectedOrderForCustomShip] = useState(null);
  const [submittingCustomShip, setSubmittingCustomShip] = useState(false);

  useEffect(() => {
    if (token && pageId) {
      fetchOrders();
    }
  }, [token, pageId, query, statusFilter, page, rowsPerPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await APIHelper.listOrders(token, {
        pageId,
        status: statusFilter === "All" ? undefined : statusFilter,
        search: query || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setOrders(response.orders || []);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (status) => {
    const statusMap = {
      pending: { label: "Pending", color: "warning" },
      paid: { label: "Paid", color: "success" },
      processing: { label: "Processing", color: "info" },
      ready_for_shipping: { label: "Ready for Shipping", color: "info" },
      shipping_label_generated: {
        label: "Label Generated",
        color: "info",
      },
      shipped: { label: "Shipped", color: "info" },
      fulfilled: { label: "Fulfilled", color: "success" },
      cancelled: { label: "Cancelled", color: "default" },
    };
    const config = statusMap[status?.toLowerCase()] || {
      label: status,
      color: "default",
    };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(orderId);
      await APIHelper.updateOrderStatus(token, orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update order status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleOpenLabelDialog = async (orderId) => {
    // Find the order to check if it already has a shipmentId
    const order = orders.find((o) => o.orderId === orderId);

    // If order already has a shipmentId, confirm before proceeding with a fresh generation
    if (order?.shipmentId) {
      if (
        !window.confirm(
          "A shipment already exists for this order. Regenerating the label will cancel the old shipment and create a new one. Do you want to proceed?"
        )
      ) {
        return;
      }
      
      // We'll proceed with resetting the state below and opening the dialog
    }

    // If no shipmentId, proceed with normal flow (check serviceability, fetch quick quote and show dialog)
    setSelectedOrderForLabel(orderId);
    setSelectedCarrier("");
    setSelectedService("");
    setAvailableCarriers([]);
    setQuickQuoteRates([]);
    setPackageWeight(null);
    setPackageDimensions({ length: null, width: null, height: null });
    setLabelDialogOpen(true);

    // Check serviceability for this order (still used as a fallback)
    try {
      setLoadingServiceability(true);
      const response = await APIHelper.checkServiceability(token, orderId);
      if (response.success && response.availableCarriers) {
        const carrierMap = {
          bluedart: "Bluedart",
          zippyybluedart: "ZippyyBluedart",
          delhivery: "Delhivery",
          dtdc: "DTDC",
          ecom: "Ecom",
          xpressbees: "Xpressbees",
          "dtdc-b2b": "DTDC-B2B",
          ats: "ATS",
          zippyyxekart: "ZippyyxEkart",
          ekartpremium: "EkartPremium",
          shadowfax: "Shadowfax",
          blitz: "Blitz",
        };

        const available = response.availableCarriers
          .map((c) => {
            const normalized = c.carrier.toLowerCase();
            return carrierMap[normalized] || c.carrier;
          })
          .filter(Boolean);

        setAvailableCarriers(available);
      }
    } catch (error) {
      console.error("Error checking serviceability:", error);
      setAvailableCarriers([]);
    } finally {
      setLoadingServiceability(false);
    }

    // Fetch quick quote rates for this order
    try {
      setLoadingQuickQuote(true);
      const quickQuoteResponse = await APIHelper.getQuickQuote(token, orderId);
      if (
        quickQuoteResponse?.success &&
        Array.isArray(quickQuoteResponse.rates)
      ) {
        setQuickQuoteRates(quickQuoteResponse.rates);
      } else {
        setQuickQuoteRates([]);
      }

      if (quickQuoteResponse?.params) {
        const p = quickQuoteResponse.params;
        setPackageWeight(p.weightKg ?? 1);
        setPackageDimensions({
          length: p.dimensionsCm?.length ?? 12,
          width: p.dimensionsCm?.width ?? 12,
          height: p.dimensionsCm?.height ?? 12,
        });
      }
    } catch (error) {
      console.error("Error fetching quick quote rates:", error);
      setQuickQuoteRates([]);
    } finally {
      setLoadingQuickQuote(false);
    }
  };

  const handleCloseLabelDialog = () => {
    setLabelDialogOpen(false);
    setSelectedOrderForLabel(null);
    setSelectedCarrier("");
    setSelectedService("");
    setAvailableCarriers([]);
    setLoadingServiceability(false);
    setQuickQuoteRates([]);
    setLoadingQuickQuote(false);
    setPackageWeight(null);
    setPackageDimensions({ length: null, width: null, height: null });
    setSelectedRate(null);
  };

  const handlePackageChange = async (pkg) => {
    setPackageWeight(pkg.weightKg);
    setPackageDimensions({
      length: pkg.length,
      width: pkg.width,
      height: pkg.height,
    });

    if (!selectedOrderForLabel) return;

    try {
      setLoadingQuickQuote(true);
      const quickQuoteResponse = await APIHelper.getQuickQuote(
        token,
        selectedOrderForLabel,
        {
          packageWeight: pkg.weightKg,
          packageDimensions: {
            length: pkg.length,
            width: pkg.width,
            height: pkg.height,
          },
        }
      );
      if (
        quickQuoteResponse?.success &&
        Array.isArray(quickQuoteResponse.rates)
      ) {
        setQuickQuoteRates(quickQuoteResponse.rates);
      } else {
        setQuickQuoteRates([]);
      }
    } catch (error) {
      console.error("Error refreshing quick quote rates:", error);
    } finally {
      setLoadingQuickQuote(false);
    }
  };

  const handleGenerateShippingLabel = async () => {
    if (!selectedCarrier || !selectedService) {
      toast.error("Please select both carrier and service");
      return;
    }

    try {
      setGeneratingLabel(selectedOrderForLabel);
      const response = await APIHelper.generateShippingLabel(
        token,
        selectedOrderForLabel,
        {
          carrier: selectedCarrier,
          service: selectedService,
          shippingRate: selectedRate || undefined,
          packageWeight: packageWeight,
          packageDimensions: packageDimensions,
        }
      );
      toast.success("Shipping label generated successfully!");
      fetchOrders();
      handleCloseLabelDialog();
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to generate shipping label"
      );
    } finally {
      setGeneratingLabel(null);
    }
  };

  const handleTrackShipment = async (orderId) => {
    try {
      const response = await APIHelper.trackShipment(token, orderId);
      setTrackingData((prev) => ({
        ...prev,
        [orderId]: response.data,
      }));
      toast.success("Tracking information updated");
      fetchOrders(); // Refresh to update status if needed
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to track shipment"
      );
    }
  };

  const handleCancelShipment = async (orderId) => {
    try {
      setCancellingShipment(orderId);
      await APIHelper.cancelShipment(token, orderId);
      toast.success("Shipment cancelled successfully");
      fetchOrders(); // Refresh to update status
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to cancel shipment"
      );
    } finally {
      setCancellingShipment(null);
      setMenuAnchor(null);
      setSelectedOrderForMenu(null);
    }
  };

  const handleOpenCustomShipDialog = (orderId) => {
    const order = orders.find((o) => o.orderId === orderId);
    setSelectedOrderForCustomShip(orderId);
    setCustomCarrier(order?.carrier || "");
    setCustomTrackingNumber(order?.trackingNumber || "");
    setCustomTrackingUrl(order?.trackingUrl || "");
    setCustomShipDialogOpen(true);
  };

  const handleCustomShipSubmit = async () => {
    if (!customCarrier.trim()) {
      toast.error("Carrier is required");
      return;
    }

    try {
      setSubmittingCustomShip(true);
      await APIHelper.customShipment(token, selectedOrderForCustomShip, {
        carrier: customCarrier.trim(),
        trackingNumber: customTrackingNumber.trim() || undefined,
        trackingUrl: customTrackingUrl.trim() || undefined,
      });
      toast.success("Custom shipping details updated successfully");
      setCustomShipDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error("Error updating custom shipment:", error);
      toast.error(error.response?.data?.error || "Failed to update custom shipment");
    } finally {
      setSubmittingCustomShip(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const toggleRowExpansion = (orderId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedRows(newExpanded);
  };

  const renderExpandedContent = (order) => {
    return (
      <TableRow>
        <TableCell
          colSpan={8}
          sx={{
            paddingBottom: 0,
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            borderBottom: "none",
            backgroundColor: "transparent",
          }}
          className="bg-gray-50 dark:bg-gray-800"
        >
          <div className="m-4 w-[calc(100%-2rem)]">
            <Grid container spacing={3}>
              {/* Customer Details */}
              <Grid item xs={12} md={6}>
                <div className="card-modern p-4 h-full">
                  <Typography
                    variant="subtitle2"
                    className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
                  >
                    <PhoneIcon fontSize="small" className="text-primary-600 dark:text-primary-400" />
                    Customer Details
                  </Typography>
                  <div className="mt-2 flex flex-col gap-3">
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Name
                      </Typography>
                      <Typography variant="body2" className="text-gray-900 dark:text-white">
                        {order.customerName || "N/A"}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Email
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Typography variant="body2" className="text-gray-900 dark:text-white">
                          {order.customerEmail || "N/A"}
                        </Typography>
                        {order.customerEmail && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              (window.location.href = `mailto:${order.customerEmail}`)
                            }
                            title="Send email"
                            className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Phone
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Typography variant="body2" className="text-gray-900 dark:text-white">
                          {order.customerPhone || "N/A"}
                        </Typography>
                        {order.customerPhone && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                `https://wa.me/${order.customerPhone.replace(
                                  /[^0-9]/g,
                                  ""
                                )}`,
                                "_blank"
                              )
                            }
                            title="Contact on WhatsApp"
                            className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <div className="card-modern p-4 h-full">
                  <Typography
                    variant="subtitle2"
                    className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
                  >
                    <LocationOnIcon fontSize="small" className="text-primary-600 dark:text-primary-400" />
                    Shipping Address
                  </Typography>
                  <div className="mt-2">
                    {order.shippingAddress ? (
                      <div className="flex flex-col gap-1">
                        {order.shippingAddress.line1 && (
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.shippingAddress.line1}
                          </Typography>
                        )}
                        {order.shippingAddress.line2 && (
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.shippingAddress.line2}
                          </Typography>
                        )}
                        <Typography variant="body2" className="text-gray-900 dark:text-white">
                          {[
                            order.shippingAddress.city,
                            order.shippingAddress.state,
                            order.shippingAddress.zipcode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                        {order.shippingAddress.country && (
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.shippingAddress.country}
                          </Typography>
                        )}
                      </div>
                    ) : (
                      <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                        No shipping address provided
                      </Typography>
                    )}
                  </div>
                </div>
              </Grid>

              {/* Shipping Information */}
              {(order.shipmentId ||
                order.trackingNumber ||
                order.shippingLabelUrl ||
                order.carrier ||
                order.service ||
                trackingData[order.orderId]) && (
                <Grid item xs={12} md={6}>
                  <div className="card-modern p-4 h-full">
                    <Typography
                      variant="subtitle2"
                      className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
                    >
                      <LocalShippingIcon fontSize="small" className="text-primary-600 dark:text-primary-400" />
                      Shipment Information
                    </Typography>
                    <div className="mt-2 flex flex-col gap-3">
                      {order.shipmentId && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Zippy Order ID
                          </Typography>
                          <Typography
                            variant="body2"
                            className="font-mono text-xs text-gray-900 dark:text-white"
                          >
                            {order.shipmentId}
                          </Typography>
                        </div>
                      )}
                      {order.trackingNumber && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Tracking Number (AWB)
                          </Typography>
                          {order.trackingUrl ? (
                            <a
                              href={order.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs text-primary-600 dark:text-primary-400 underline hover:text-primary-700 dark:hover:text-primary-300"
                            >
                              {order.trackingNumber}
                            </a>
                          ) : (
                            <Typography
                              variant="body2"
                              className="font-mono text-xs text-gray-900 dark:text-white"
                            >
                              {order.trackingNumber}
                            </Typography>
                          )}
                        </div>
                      )}
                      {order.carrier && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Carrier
                          </Typography>
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.carrier}
                          </Typography>
                        </div>
                      )}
                      {order.service && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Service
                          </Typography>
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.service}
                          </Typography>
                        </div>
                      )}
                      {order.shippingRate && (
                        <div className="flex items-center gap-2">
                          <div>
                            <Typography
                              variant="caption"
                              className="text-gray-600 dark:text-gray-400 text-xs"
                            >
                              Rate
                            </Typography>
                            <Typography variant="body2" className="text-gray-900 dark:text-white">
                              {order.shippingRate.currency || "INR"}{" "}
                              {order.shippingRate.amount || "—"}
                              {order.shippingRate.estDeliveryDays != null
                                ? ` • ETA: ${
                                    order.shippingRate.estDeliveryDays
                                  } day${
                                    order.shippingRate.estDeliveryDays === 1
                                      ? ""
                                      : "s"
                                  }`
                                : ""}
                            </Typography>
                          </div>
                          {order.shipmentId && (
                            <IconButton
                              size="small"
                              aria-label="Download invoice"
                              onClick={() => {
                                let invoiceUrl =
                                  (process.env.REACT_APP_BACKEND_URL ||
                                    "https://instashop-6a53aa420501.herokuapp.com") +
                                  `/shipping/invoice/${order.orderId}`;
                                const token = localStorage.getItem("token");
                                if (token) {
                                  const sep = invoiceUrl.includes("?")
                                    ? "&"
                                    : "?";
                                  invoiceUrl = `${invoiceUrl}${sep}token=${encodeURIComponent(
                                    token
                                  )}`;
                                }
                                window.open(
                                  invoiceUrl,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              }}
                              className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                      )}
                      {order.shippingPackage && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Package (Booked)
                          </Typography>
                          <Typography variant="body2" className="text-gray-900 dark:text-white">
                            {order.shippingPackage.weightKg
                              ? `${order.shippingPackage.weightKg} kg`
                              : "—"}{" "}
                            {order.shippingPackage.lengthCm &&
                            order.shippingPackage.widthCm &&
                            order.shippingPackage.heightCm
                              ? `• ${order.shippingPackage.lengthCm} x ${order.shippingPackage.widthCm} x ${order.shippingPackage.heightCm} cm`
                              : ""}
                          </Typography>
                        </div>
                      )}
                      {order.shippingLabelUrl && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Shipping Label
                          </Typography>
                          <div className="mt-2">
                            <Button
                              size="small"
                              variant="outlined"
                              href={(() => {
                                if (!order.shippingLabelUrl) return null;

                                let labelUrl = order.shippingLabelUrl;

                                // Legacy fix: If URL contains Zippy shipment ID, regenerate with our orderId
                                // This handles old URLs saved before the fix
                                if (labelUrl.includes("/ord_")) {
                                  const backendUrl =
                                    process.env.REACT_APP_BACKEND_URL ||
                                    "https://instashop-6a53aa420501.herokuapp.com";
                                  labelUrl = `${backendUrl}/shipping/label/${order.orderId}`;
                                }

                                // Legacy fix: Remove double /api/api/ from old URLs
                                if (labelUrl.includes("/api/api/")) {
                                  labelUrl = labelUrl.replace(
                                    "/api/api/",
                                    "/api/"
                                  );
                                }

                                // Legacy fix: Replace www.reeltale.shop with backend URL
                                // www.reeltale.shop routes /api/* to frontend, backend is at Heroku
                                if (labelUrl.includes("reeltale.shop")) {
                                  const backendUrl =
                                    process.env.REACT_APP_BACKEND_URL ||
                                    "https://instashop-6a53aa420501.herokuapp.com";
                                  labelUrl = labelUrl.replace(
                                    /https?:\/\/[^/]+/,
                                    backendUrl
                                  );
                                  labelUrl = labelUrl.replace(
                                    "/api/shipping",
                                    "/shipping"
                                  );
                                }

                                // Add token as query parameter for authentication
                                // Required because browser links don't send Authorization headers
                                const token = localStorage.getItem("token");
                                if (token) {
                                  const separator = labelUrl.includes("?")
                                    ? "&"
                                    : "?";
                                  return `${labelUrl}${separator}token=${encodeURIComponent(
                                    token
                                  )}`;
                                }
                                return labelUrl;
                              })()}
                              target="_blank"
                              startIcon={<DownloadIcon />}
                              className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
                            >
                              Download Label
                            </Button>
                          </div>
                        </div>
                      )}
                      {trackingData[order.orderId] && (
                        <div>
                          <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                            Tracking Status
                          </Typography>
                          <div className="mt-2">
                            <Chip
                              label={
                                trackingData[order.orderId].status || "Unknown"
                              }
                              color="info"
                              size="small"
                              className="mr-2"
                            />
                            {trackingData[order.orderId].location && (
                              <Typography variant="body2" className="text-gray-900 dark:text-white mt-2">
                                Location: {trackingData[order.orderId].location}
                              </Typography>
                            )}
                            {trackingData[order.orderId].estimatedDelivery && (
                              <Typography
                                variant="body2"
                                className="text-gray-600 dark:text-gray-400 mt-1"
                              >
                                Est. Delivery:{" "}
                                {new Date(
                                  trackingData[order.orderId].estimatedDelivery
                                ).toLocaleDateString()}
                              </Typography>
                            )}
                          </div>
                        </div>
                      )}
                      {!order.shipmentId &&
                        !order.trackingNumber &&
                        !order.carrier &&
                        !order.shippingLabelUrl &&
                        !trackingData[order.orderId] && (
                          <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                            No shipment information available
                          </Typography>
                        )}
                      {order.cancellationErrors?.shipmentCancellationError && (
                        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                          <Typography variant="caption" className="font-bold text-yellow-900 dark:text-yellow-200 block mb-1">
                            Shipment Cancellation Failed:
                          </Typography>
                          <Typography variant="body2" className="text-yellow-800 dark:text-yellow-200">
                            {order.cancellationErrors.shipmentCancellationError}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                </Grid>
              )}

              {/* Payment Information */}
              <Grid item xs={12} md={6}>
                <div className="card-modern p-4 h-full">
                  <Typography
                    variant="subtitle2"
                    className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
                  >
                    <PaymentIcon fontSize="small" className="text-primary-600 dark:text-primary-400" />
                    Payment Information
                  </Typography>
                  <div className="mt-2 flex flex-col gap-3">
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Payment ID
                      </Typography>
                      <Typography
                        variant="body2"
                        className="font-mono text-xs text-gray-900 dark:text-white"
                      >
                        {order.razorpayPaymentId ||
                          order.razorpayOrderId ||
                          "N/A"}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Post ID
                      </Typography>
                      <Typography
                        variant="body2"
                        className="font-mono text-xs text-gray-900 dark:text-white"
                      >
                        {order.postId || "N/A"}
                      </Typography>
                    </div>
                    {order.commentId && (
                      <div>
                        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                          Comment by
                        </Typography>
                        <Typography variant="body2" className="text-gray-900 dark:text-white">
                          @{order.username || "N/A"}
                        </Typography>
                      </div>
                    )}
                    <div>
                      <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                        Currency
                      </Typography>
                      <Typography variant="body2" className="text-gray-900 dark:text-white">
                        {order.currency || "INR"}
                      </Typography>
                    </div>
                    {order.razorpayRefundId && (
                      <div>
                        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                          Refund ID
                        </Typography>
                        <Typography
                          variant="body2"
                          className="font-mono text-xs text-green-600 dark:text-green-400"
                        >
                          {order.razorpayRefundId}
                        </Typography>
                      </div>
                    )}
                    {order.cancellationErrors?.refundError && (
                      <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                        <Typography variant="caption" className="font-bold text-red-900 dark:text-red-200 block mb-1">
                          Refund Failed:
                        </Typography>
                        <Typography variant="body2" className="text-red-800 dark:text-red-200">
                          {order.cancellationErrors.refundError}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </Grid>

              {/* Campaign Information */}
              {order.campaignId && (
                <Grid item xs={12} md={6}>
                  <div className="card-modern p-4 h-full border-primary-100 dark:border-primary-900 bg-primary-50/10">
                    <Typography
                      variant="subtitle2"
                      className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
                    >
                      <CampaignIcon fontSize="small" className="text-primary-600 dark:text-primary-400" />
                      Campaign Information
                    </Typography>
                    <div className="mt-2 flex flex-col gap-3">
                      <div>
                        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                          Campaign Name
                        </Typography>
                        <Typography variant="body2" className="text-gray-900 dark:text-white font-semibold">
                          {order.campaignName || "Creator Campaign"}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 text-xs">
                          Influencer
                        </Typography>
                        <Typography variant="body2" className="text-primary-600 dark:text-primary-400 font-medium">
                          {order.influencerName || "Influencer"}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (!pageId) {
    return (
      <div className="card-modern p-6">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
          <Typography className="text-sm text-blue-800 dark:text-blue-200">
            ℹ️ Please select a page to view orders.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <Box>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <ShoppingCartIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
          Orders
        </Typography>
      </div>

      {/* Search and Filter */}
      <div className="card-modern p-4 mt-6">
        <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-4">
          View and manage orders from Instagram comments
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="md:col-span-4">
            <TextField
              fullWidth
              size="small"
              label="Search by Order ID / Customer"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              className="bg-white dark:bg-gray-800"
            />
          </div>
          <div className="md:col-span-3">
            <FormControl fullWidth size="small">
              <InputLabel id="order-status-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
              <Select
                labelId="order-status-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                className="bg-white dark:bg-gray-800"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="ready_for_shipping">
                  Ready for Shipping
                </MenuItem>
                <MenuItem value="shipping_label_generated">
                  Shipping Label Generated
                </MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="fulfilled">Fulfilled</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card-modern p-4 mt-6">
        <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Orders ({total})
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="📦"
            title="No orders yet"
            description="Orders will appear here when customers purchase from your posts. Make sure your Auto DMs are configured to start selling!"
            ctaText="View Setup Guide"
            ctaAction="/dashboard/comments/configure"
            variant="secondary"
          />
        ) : (
          <>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed", // Prevent layout shift
                  width: "100%",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }} />
                    <TableCell sx={{ width: 160 }} className="text-gray-700 dark:text-gray-300 font-semibold">Order ID</TableCell>
                    <TableCell sx={{ width: 140 }} className="text-gray-700 dark:text-gray-300 font-semibold">Customer</TableCell>
                    <TableCell sx={{ width: 150 }} className="text-gray-700 dark:text-gray-300 font-semibold">Product</TableCell>
                    <TableCell align="right" sx={{ width: 100 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                      Amount
                    </TableCell>
                    <TableCell sx={{ width: 120 }} className="text-gray-700 dark:text-gray-300 font-semibold">Status</TableCell>
                    <TableCell sx={{ width: 100 }} className="text-gray-700 dark:text-gray-300 font-semibold">Payment Date</TableCell>
                    <TableCell align="right" sx={{ width: 200 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <React.Fragment key={order.orderId}>
                      <TableRow
                        hover
                        onClick={() => toggleRowExpansion(order.orderId)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? theme.palette.action.hover
                                : theme.palette.action.hover,
                          },
                        }}
                      >
                        <TableCell
                          sx={{ width: 50 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(order.orderId)}
                            sx={{ padding: 0.5 }}
                          >
                            {expandedRows.has(order.orderId) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{order.orderId}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 130 }}
                              title={order.customerName || "N/A"}
                            >
                              {order.customerName || "N/A"}
                            </Typography>
                            {order.customerEmail && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: 130 }}
                                title={order.customerEmail}
                              >
                                {order.customerEmail}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 140 }}
                                title={order.productName || "N/A"}
                              >
                                {order.productName || "N/A"}
                              </Typography>
                              {order.quantity && order.quantity > 1 && (
                                <Chip
                                  label={`x${order.quantity}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            
                            {order.variants && order.variants.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {order.variants.map((v, i) => (
                                  <Chip 
                                    key={i} 
                                    label={`${v.option}: ${v.value}`} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ height: 20, fontSize: '0.7rem' }} 
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            order.amountInRupees || order.amount / 100
                          )}
                        </TableCell>
                        <TableCell>{statusChip(order.status)}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 90 }}
                            title={formatDate(
                              order.paymentDate || order.createdAt
                            )}
                          >
                            {formatDate(order.paymentDate || order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ width: 200, paddingRight: 1 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              justifyContent: "flex-end",
                              flexWrap: "nowrap",
                            }}
                          >
                            {order.status === "paid" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStatus(
                                    order.orderId,
                                    "processing"
                                  );
                                }}
                                disabled={updatingStatus === order.orderId}
                                startIcon={
                                  updatingStatus === order.orderId ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )
                                }
                                sx={{
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  px: 1,
                                }}
                              >
                                Processing
                              </Button>
                            )}
                            {order.status === "processing" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStatus(
                                    order.orderId,
                                    "ready_for_shipping"
                                  );
                                }}
                                disabled={updatingStatus === order.orderId}
                                startIcon={
                                  updatingStatus === order.orderId ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )
                                }
                                sx={{
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  px: 1,
                                }}
                              >
                                Ready to Ship
                              </Button>
                            )}
                            {order.status === "ready_for_shipping" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenLabelDialog(order.orderId);
                                }}
                                disabled={generatingLabel === order.orderId}
                                startIcon={
                                  generatingLabel === order.orderId ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )
                                }
                                sx={{
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  px: 1,
                                }}
                              >
                                Generate Label
                              </Button>
                            )}
                            {order.status === "shipping_label_generated" && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenLabelDialog(order.orderId);
                                  }}
                                  disabled={generatingLabel === order.orderId}
                                  startIcon={
                                    generatingLabel === order.orderId ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CheckCircleIcon fontSize="small" />
                                    )
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    fontSize: "0.75rem",
                                    px: 1,
                                    mr: 0.5,
                                  }}
                                >
                                  Regenerate
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateStatus(order.orderId, "shipped");
                                  }}
                                  disabled={updatingStatus === order.orderId}
                                  startIcon={
                                    updatingStatus === order.orderId ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CheckCircleIcon fontSize="small" />
                                    )
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    fontSize: "0.75rem",
                                    px: 1,
                                  }}
                                >
                                  Ship
                                </Button>
                              </>
                            )}
                            {order.status === "shipped" && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStatus(
                                    order.orderId,
                                    "fulfilled"
                                  );
                                }}
                                disabled={updatingStatus === order.orderId}
                                startIcon={
                                  updatingStatus === order.orderId ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )
                                }
                                sx={{
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  px: 1,
                                }}
                              >
                                Mark Fulfilled
                              </Button>
                            )}
                            {order.status !== "cancelled" && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuAnchor(e.currentTarget);
                                  setSelectedOrderForMenu(order.orderId);
                                }}
                                sx={{ ml: 0.5 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(order.orderId) &&
                        renderExpandedContent(order)}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="mt-4">
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                className="text-gray-700 dark:text-gray-300"
              />
            </div>
          </>
        )}
      </div>

      {/* Carrier/Service Selection Dialog */}
      <CarrierServiceDialog
        open={labelDialogOpen}
        onClose={handleCloseLabelDialog}
        selectedCarrier={selectedCarrier}
        selectedService={selectedService}
        onCarrierChange={(value) => {
          setSelectedCarrier(value);
          setSelectedService(""); // Reset service when carrier changes
        }}
        onServiceChange={setSelectedService}
        availableCarriers={availableCarriers}
        loadingServiceability={loadingServiceability}
        quickQuoteRates={quickQuoteRates}
        loadingQuickQuote={loadingQuickQuote}
        packageWeight={packageWeight}
        packageDimensions={packageDimensions}
        onPackageChange={handlePackageChange}
        onRateSelect={setSelectedRate}
        generatingLabel={!!generatingLabel}
        onGenerate={handleGenerateShippingLabel}
        componentName="OrdersPage"
        productName={
          selectedOrderForLabel
            ? orders.find((o) => o.orderId === selectedOrderForLabel)
                ?.productName || null
            : null
        }
      />

      {/* Actions Overflow Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setSelectedOrderForMenu(null);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedOrderForMenu &&
          (() => {
            const selectedOrder = orders.find(
              (o) => o.orderId === selectedOrderForMenu
            );
            const hasShippingLabel =
              selectedOrder?.shippingLabelUrl ||
              selectedOrder?.status === "shipping_label_generated";
            const showCustomShipment =
              selectedOrder &&
              ["ready_for_shipping", "shipping_label_generated"].includes(
                selectedOrder.status
              );

            return (
              <>
                {showCustomShipment && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedOrderForMenu) {
                        handleOpenCustomShipDialog(selectedOrderForMenu);
                      }
                      setMenuAnchor(null);
                      setSelectedOrderForMenu(null);
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalShippingIcon fontSize="small" />
                      Custom Shipment
                    </Box>
                  </MenuItem>
                )}
                {/* Cancel Shipment - Commented out for now, keeping code for later use */}
                {hasShippingLabel && (
                  <>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedOrderForMenu) {
                          handleOpenLabelDialog(selectedOrderForMenu);
                        }
                        setMenuAnchor(null);
                        setSelectedOrderForMenu(null);
                      }}
                      disabled={generatingLabel === selectedOrderForMenu}
                    >
                      {generatingLabel === selectedOrderForMenu ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={14} />
                          Regenerating Label...
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocalShippingIcon fontSize="small" />
                          Regenerate Label
                        </Box>
                      )}
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedOrderForMenu) {
                          handleCancelShipment(selectedOrderForMenu);
                        }
                      }}
                      disabled={cancellingShipment === selectedOrderForMenu}
                    >
                      {cancellingShipment === selectedOrderForMenu ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={14} />
                          Cancelling Shipment...
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CancelIcon fontSize="small" />
                          Cancel Shipment
                        </Box>
                      )}
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedOrderForMenu) {
                      handleUpdateStatus(selectedOrderForMenu, "cancelled");
                    }
                    setMenuAnchor(null);
                    setSelectedOrderForMenu(null);
                  }}
                  disabled={updatingStatus === selectedOrderForMenu}
                >
                  {updatingStatus === selectedOrderForMenu ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={14} />
                      Cancelling...
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CancelIcon fontSize="small" />
                      Cancel Order
                    </Box>
                  )}
                </MenuItem>
              </>
            );
          })()}
      </Menu>

      {/* Custom Shipment Dialog */}
      <Dialog
        open={customShipDialogOpen}
        onClose={() => setCustomShipDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Custom Shipment Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Courier / Carrier"
              variant="outlined"
              fullWidth
              value={customCarrier}
              onChange={(e) => setCustomCarrier(e.target.value)}
              placeholder="e.g. FedEx, DTDC, DHL, Self"
              required
              error={!customCarrier.trim()}
              helperText={!customCarrier.trim() ? "Carrier name is required" : ""}
            />
            <TextField
              label="Tracking Number (AWB)"
              variant="outlined"
              fullWidth
              value={customTrackingNumber}
              onChange={(e) => setCustomTrackingNumber(e.target.value)}
              placeholder="e.g. 1234567890"
            />
            <TextField
              label="Tracking URL"
              variant="outlined"
              fullWidth
              value={customTrackingUrl}
              onChange={(e) => setCustomTrackingUrl(e.target.value)}
              placeholder="e.g. https://carrier.com/track/..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomShipDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCustomShipSubmit}
            variant="contained"
            color="primary"
            disabled={submittingCustomShip || !customCarrier.trim()}
            startIcon={submittingCustomShip ? <CircularProgress size={16} /> : null}
          >
            Mark Shipped
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
